import React from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Autocomplete,
  IconButton,
  Chip,
  Collapse,
  TablePagination,
  Link,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { Transfer, TransferStatus, TransferItem } from '../services/transfers.service';
import { formatCurrency } from '../utils/currency';

interface TransferHistoryTabProps {
  historyTransfers: Transfer[];
  historyPagination: any;
  historyPage: number;
  setHistoryPage: (page: number) => void;
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
  statusFilter: TransferStatus | '';
  setStatusFilter: (status: TransferStatus | '') => void;
  fromLocationFilter: string;
  setFromLocationFilter: (location: string) => void;
  toLocationFilter: string;
  setToLocationFilter: (location: string) => void;
  dateFromFilter: Date | null;
  setDateFromFilter: (date: Date | null) => void;
  dateToFilter: Date | null;
  setDateToFilter: (date: Date | null) => void;
  locations: any[];
  refetchHistory: () => void;
  clearFilters: () => void;
  handleViewDetail: (transfer: Transfer) => void;
  getStatusColor: (status: TransferStatus) => any;
  getStatusIcon: (status: TransferStatus) => React.ReactNode;
  getTotalItems: (items: TransferItem[]) => number;
  getTotalValue: (items: TransferItem[]) => number;
}

const TransferHistoryTab: React.FC<TransferHistoryTabProps> = ({
  historyTransfers,
  historyPagination,
  historyPage,
  setHistoryPage,
  showFilters,
  setShowFilters,
  statusFilter,
  setStatusFilter,
  fromLocationFilter,
  setFromLocationFilter,
  toLocationFilter,
  setToLocationFilter,
  locations,
  refetchHistory,
  clearFilters,
  handleViewDetail,
  getStatusColor,
  getStatusIcon,
  getTotalItems,
  getTotalValue,
}) => {
  return (
    <Box>
      {/* History with Filters */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Historial de Transferencias</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => setShowFilters(!showFilters)}
            >
              Filtros
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => refetchHistory()}
            >
              Actualizar
            </Button>
          </Box>
        </Box>
        
        <Collapse in={showFilters}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Estado</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Estado"
                    onChange={(e) => setStatusFilter(e.target.value as TransferStatus)}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {Object.values(TransferStatus).map((status) => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Autocomplete
                  size="small"
                  options={locations}
                  getOptionLabel={(option) => option.name}
                  value={locations.find(l => l.id === fromLocationFilter) || null}
                  onChange={(_, location) => setFromLocationFilter(location?.id || '')}
                  renderInput={(params) => (
                    <TextField {...params} label="Ubicación Origen" />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Autocomplete
                  size="small"
                  options={locations}
                  getOptionLabel={(option) => option.name}
                  value={locations.find(l => l.id === toLocationFilter) || null}
                  onChange={(_, location) => setToLocationFilter(location?.id || '')}
                  renderInput={(params) => (
                    <TextField {...params} label="Ubicación Destino" />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => {
                      setHistoryPage(1);
                      refetchHistory();
                    }}
                  >
                    Aplicar
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={clearFilters}
                  >
                    Limpiar
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Collapse>
      </Box>

      {/* History Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Número</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Origen</TableCell>
              <TableCell>Destino</TableCell>
              <TableCell>Usuario</TableCell>
              <TableCell>Items</TableCell>
              <TableCell>Total</TableCell>
              <TableCell>Fecha</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {historyTransfers.map((transfer) => (
              <TableRow key={transfer.id}>
                <TableCell>
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => handleViewDetail(transfer)}
                    sx={{ fontWeight: 'bold', textDecoration: 'none' }}
                  >
                    {transfer.transferNumber || `DRAFT-${transfer.id.slice(0, 8)}`}
                  </Link>
                </TableCell>
                <TableCell>
                  <Chip
                    icon={getStatusIcon(transfer.status) as React.ReactElement}
                    label={transfer.status}
                    color={getStatusColor(transfer.status)}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>{transfer.fromLocation.name}</TableCell>
                <TableCell>{transfer.toLocation.name}</TableCell>
                <TableCell>{transfer.user?.name || 'N/A'}</TableCell>
                <TableCell>
                  {transfer.items.length} productos
                  <Typography variant="caption" display="block" color="text.secondary">
                    ({getTotalItems(transfer.items)} unidades)
                  </Typography>
                </TableCell>
                <TableCell>
                  {formatCurrency(getTotalValue(transfer.items))}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {new Date(transfer.createdAt).toLocaleDateString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(transfer.createdAt).toLocaleTimeString()}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Tooltip title="Ver detalles">
                    <IconButton
                      size="small"
                      onClick={() => handleViewDetail(transfer)}
                    >
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {historyPagination && (
          <TablePagination
            component="div"
            count={historyPagination.total}
            page={historyPage - 1}
            onPageChange={(_, newPage) => setHistoryPage(newPage + 1)}
            rowsPerPage={historyPagination.limit}
            onRowsPerPageChange={() => {}}
            rowsPerPageOptions={[historyPagination.limit]}
          />
        )}
      </TableContainer>
    </Box>
  );
};

export default TransferHistoryTab;